import axios from 'axios';

// Same-origin API: each v2 site's CloudFront routes /api/* to its own
// backend's API Gateway, so the environment is baked into each distribution
// and the frontend no longer needs to detect it. In `yarn dev` the vite proxy
// plays CloudFront's part (see DEV_API_PROXY_TARGET in vite.config.mjs).
const api = axios.create({
    baseURL: '/api',
});

api.interceptors.request.use(
    (config) => {
        config.headers['Authorization'] = `Bearer ${localStorage.getItem('token')}`;
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Endpoints that run without a session. A 401 from these is a verdict on the
// credentials just submitted — a wrong password, a stale reset link — not on an
// expired session, so they must never trigger the redirect below. Exempting
// /login in particular is what stops a failed sign-in from bouncing back to the
// sign-in page in a loop.
const UNAUTHENTICATED_PATHS = ['/login', '/signup', '/verify', '/forgot', '/reset'];

const isUnauthenticatedRequest = (url = '') => UNAUTHENTICATED_PATHS.some((path) => url.split('?')[0] === path);

// Several requests can be in flight when a token expires; they'd each try to
// navigate. Latch so only the first one does.
let redirectingToLogin = false;

/**
 * Session handling for failed responses.
 *
 * 401 — the token is missing, malformed or expired. Until now nothing acted on
 * this: the route guard only checks expiry when a route mounts, so an expired
 * token survived until the next navigation and the user sat on a page of empty
 * tables. Drop the dead token and send them to /login, preserving where they
 * were in `?redirect=` so a future change can return them there after signing in.
 *
 * 403 — deliberately passed through to the caller rather than handled here.
 * This API does not currently emit 403 at all (authorization failures come back
 * as HTTP 200 with `status: "fail"`), and api.ts is a plain module with no React
 * tree, so rendering a Snackbar from it would mean standing up global UI state
 * for a case the backend never produces. Callers that need to explain a refusal
 * already have the rejected promise.
 */
api.interceptors.response.use(
    (response) => response,
    (error) => {
        const status = error?.response?.status;
        const isSessionExpiry = status === 401 && !isUnauthenticatedRequest(error?.config?.url);

        if (isSessionExpiry && !redirectingToLogin) {
            localStorage.removeItem('token');
            const { pathname, search } = window.location;
            if (pathname !== '/login') {
                redirectingToLogin = true;
                const from = encodeURIComponent(`${pathname}${search}`);
                window.location.assign(`/login?redirect=${from}`);
            }
        }

        return Promise.reject(error);
    }
);

const getRecords = async () => {
    const { data } = await api.get('/records');
    return data.data;
};

const getNRecords = async (offset: number, limit: number, start_time: string, end_time: string) => {
    // let req = {params: {offset, limit}};
    let req = { offset, limit, start_time, end_time };
    // const { data } = await api.get('/nrecords', req);
    const { data } = await api.post('/getRecords', req);
    return data.data;
};

const getNotStarted = async (offset: number, limit: number, start_time: string, end_time: string) => {
    // let req = {params: {offset, limit}};
    let req = { offset, limit, start_time, end_time };
    // const { data } = await api.get('/nrecords', req);
    const { data } = await api.post('/getNotStarted', req);
    return data.data;
};

const getRealTime = async (start_time: string, end_time: string, archived = false) => {
    // Real-time page: never serve a cached response, or a refetch right after a change
    // (e.g. group membership) can come back stale and not reflect it.
    const { data } = await api.get('/realtime/records', {
        params: { start_time, end_time, archived, _t: Date.now() },
        headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
    });
    return data.data;
};

const toggleRealtimeArchive = async (device_id: string, archived: boolean) => {
    const { data } = await api.put(`/realtime/record/${device_id}/archive`, { archived });
    return data;
};

const getRecordById = async (device_id: string, index: number) => {
    let req = { device_id, index };
    const { data, status } = await api.post('/getRecordById', req);
    return { statusCode: status, body: data.data };
};

const getRealTimeById = async (device_id: string, start_time: string, end_time: string) => {
    let params = { device_id, start_time, end_time };
    const { data } = await api.get('/realtime/record', { params });
    return data.data;
};

const getReportData = async (device_id: string, index: number) => {
    let req = { device_id, index };
    const { data, status } = await api.post('/getReportData', req);
    return { statusCode: status, body: data.data };
};

const updateRecord = async (device_id: string, index: number, record: any) => {
    let req = { device_id, index, record };
    const { data } = await api.put(`/updateRecord`, req);
    return data;
};

const updateRecords = async (device_ids: string[], indexs: number[], record: any) => {
    let req = { device_ids, indexs, record };
    const { data } = await api.put(`/updateRecords`, req);
    return data;
};

const updateRealtime = async (device_ids: string[], record: any) => {
    let req = { device_ids, record };
    const { data } = await api.put(`/updateRealtime`, req);
    return data;
};

const toggleRealtimeEmailNotification = async (device_id: string, enabled: boolean) => {
    const { data } = await api.put(`/realtime/record/${device_id}/notification/email`, { enabled });
    return data;
};

const toggleRealtimeAppNotification = async (device_id: string, enabled: boolean) => {
    const { data } = await api.put(`/realtime/record/${device_id}/notification/app`, { enabled });
    return data;
};

const toggleRealtimeLineNotification = async (device_id: string, enabled: boolean) => {
    const { data } = await api.put(`/realtime/record/${device_id}/notification/line`, { enabled });
    return data;
};

// --- Realtime device groups ---
// Vendor accounts receive groups across every owned company, each carrying
// company_id/company_name for "Company Name - Group Name" rendering.
const getRealtimeGroups = async () => {
    // The group list changes as groups are created/deleted, so never serve a cached
    // response — a stale empty list would hide groups that already exist on the server.
    const { data } = await api.get('/realtime/groups', {
        headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
        params: { _t: Date.now() },
    });
    return data; // { groups, status }
};

const createRealtimeGroup = async (name: string) => {
    const { data } = await api.post('/realtime/groups', { name });
    return data; // { group, groups, status } | { status: 'fail', message }
};

const updateRealtimeGroup = async (group_id: string, name: string) => {
    const { data } = await api.put(`/realtime/groups/${group_id}`, { name });
    return data; // { groups, status } | { status: 'fail', message }
};

const deleteRealtimeGroup = async (group_id: string) => {
    const { data } = await api.delete(`/realtime/groups/${group_id}`);
    return data; // { groups, status } | { status: 'fail', message }
};

// Batch membership: device ids in `add` join, ids in `remove` leave (remove wins).
const updateRealtimeGroupDevices = async (group_id: string, add: string[], remove: string[]) => {
    const { data } = await api.put(`/realtime/groups/${group_id}/devices`, { add, remove });
    return data; // { devices: [{ device_id, result }], status }
};

const login = async (email: string, password: string) => {
    const req = { email, password };
    const { data } = await api.post('/login', req);
    return data;
};

const signup = async (
    email: string,
    password: string,
    display_name: string,
    country_code: string,
    phone_number: string,
    language: string
) => {
    const req = { email, password, display_name, country_code, phone_number, language };
    const { data } = await api.post('/signup', req);
    return data;
};

const verify = async (email: string, token: string) => {
    const req = { email, token };
    const { data } = await api.post('/verify', req);
    return data;
};

const forgot = async (email: string, language: string) => {
    const req = { email, language };
    const { data } = await api.post('/forgot', req);
    return data;
};

const reset = async (email: string, token: string, password: string) => {
    const req = { email, token, password };
    const { data } = await api.post('/reset', req);
    return data;
};

const getInformation = async () => {
    const { data } = await api.get('/users/me');
    return data;
};

const getInformationById = async (id: string) => {
    const { data } = await api.get(`/information/${id}`);
    return data;
};
const updateInformation = async (newInfo: any) => {
    // const req = {company_name, telephone, mobilephone, email, address, website_url, lineaccount, note}
    const { data } = await api.put('/users/me', { newInfo });
    return data;
};

const getOverview = async (start_time: string, end_time: string) => {
    const req = { start_time, end_time };
    const { data } = await api.post('/getOverview', req);
    return data;
};

const addRecord = async (device_ids: string[]) => {
    const req = { device_ids };
    const { data } = await api.post('/addRecord', req);
    return data;
};

const addRealTime = async (device_ids: string[]) => {
    const req = { device_ids };
    const { data } = await api.post('/addRealTime', req);
    return data;
};

const deleteNotStarted = async (device_ids: string[]) => {
    const req = { device_ids };
    const { data } = await api.post('/deleteNotStarted', req);
    return data;
};

const deleteRecords = async (device_indexs: any) => {
    const req = { device_indexs };
    const { data } = await api.post('/deleteRecords', req);
    return data;
};

const getLabels = async () => {
    const { data } = await api.get('/labels');
    return data;
};

const createLabel = async (name: string, color: string) => {
    const payload: { name: any; color?: any } = { name };
    if (color) {
        payload.color = color;
    }
    const { data } = await api.post('/labels', payload);
    return data;
};

type LabelUpdate = { name: string; color?: string } | { name?: string; color: string };
const updateLabel = async (id: string, update: LabelUpdate) => {
    const { data } = await api.put(`/labels/${id}`, update);
    return data;
};

const deleteLabel = async (id: string) => {
    const { data } = await api.delete(`/labels/${id}`);
    return data;
};

const getLabelByDeviceID = async (device_id: string, index: number) => {
    const { data } = await api.get(`/records/${device_id}/${index}/labels`);
    return data;
};

const addLabelsToRecord = async (device_id: string, index: number, label_ids: any) => {
    const { data } = await api.post(`/records/${device_id}/${index}/labels`, { label_ids });
    return data;
};

const removeLabelsFromRecord = async (device_id: string, index: number, label_ids: any) => {
    // Item-level DELETE for each label ID
    const results = await Promise.all(
        label_ids.map((id: string) => api.delete(`/records/${device_id}/${index}/labels/${id}`).then((r) => r.data))
    );
    return results[results.length - 1];
};

/**
 * Get presigned S3 URL for PDF report (supports both original and realtime report types)
 * @param {string} deviceId - Device ID
 * @param {number} index - Device index (-1 for latest)
 * @param {string} timeZone - Timezone string (e.g., 'UTC+08:00')
 * @param {string} lang - Language code (e.g., 'en', 'ja', 'zh')
 * @param {boolean} regenerate - Whether to regenerate the PDF
 * @param {Object} [extraParams] - Optional extra query params (e.g., report_type, start_time, end_time)
 * @returns {Promise<{status: string, url: string, cached?: boolean}>}
 */
/**
 * Get presigned S3 URL for Excel report
 * @param {string} deviceId - Device ID
 * @param {number|string} index - Device index (-1 for latest)
 * @param {string} timeZone - Timezone string (e.g., 'UTC+08:00')
 * @param {string} lang - Language code (e.g., 'en', 'ja', 'zh')
 * @param {boolean} regenerate - Whether to regenerate the Excel file
 * @param {Object} [extraParams] - Optional extra query params (e.g., report_type, start_time, end_time)
 * @returns {Promise<{status: string, url: string, cached?: boolean}>}
 */
const getReportExcelUrl = async (
    deviceId: string,
    index: any = -1,
    timeZone = 'UTC+00:00',
    lang = 'en',
    regenerate = false,
    extraParams = {}
) => {
    const params = new URLSearchParams({
        timeZone,
        lang,
        regenerate: String(regenerate),
        ...extraParams,
    });
    const url = `/reports/excel/${deviceId}/${index}?${params.toString()}`;
    const response = await api.get(url);
    if (response.data && typeof response.data === 'object' && 'data' in response.data && 'status' in response.data) {
        return { ...response.data.data, status: response.data.status };
    }
    return response.data;
};

const getReportPdfUrl = async (
    deviceId: string,
    index: any = -1,
    timeZone = 'UTC+00:00',
    lang = 'en',
    regenerate = false,
    extraParams = {}
) => {
    const params = new URLSearchParams({
        timeZone,
        lang,
        regenerate: String(regenerate),
        ...extraParams,
    });
    const url = `/reports/pdf/${deviceId}/${index}?${params.toString()}`;
    const response = await api.get(url);
    // If backend wraps as { status, data }, unwrap to return data and status
    if (response.data && typeof response.data === 'object' && 'data' in response.data && 'status' in response.data) {
        return { ...response.data.data, status: response.data.status };
    }
    return response.data;
};

const getCertPdfUrl = async (deviceId: any, timeZone = 'UTC+00:00', lang = 'en', regenerate = false) => {
    const params = new URLSearchParams({
        timeZone,
        lang,
        regenerate: String(regenerate),
    });
    const url = `/reports/cert/${deviceId}?${params.toString()}`;
    const response = await api.get(url);
    if (response.data && typeof response.data === 'object' && 'data' in response.data && 'status' in response.data) {
        return { ...response.data.data, status: response.data.status };
    }
    return response.data;
};

const getLineStatus = async () => {
    const { data } = await api.get('/line/status');
    return data; // { status, bound, accounts: [{ line_user_id, display_name, picture_url }] }
};

const createLineBindCode = async () => {
    const { data } = await api.post('/line/bind-code');
    return data; // { status, code, expires_in }
};

// Pass a line_user_id to unlink a single account; omit it to unlink every linked account.
const unlinkLine = async (lineUserId: any) => {
    const config = lineUserId ? { params: { line_user_id: lineUserId } } : undefined;
    const { data } = await api.delete('/line/bind', config);
    return data; // { status }
};

export {
    getRecords,
    getNRecords,
    getNotStarted,
    getRealTime,
    getRecordById,
    getRealTimeById,
    getReportData,
    login,
    signup,
    verify,
    forgot,
    reset,
    getInformation,
    getInformationById,
    updateRecord,
    updateRecords,
    updateRealtime,
    toggleRealtimeEmailNotification,
    toggleRealtimeAppNotification,
    toggleRealtimeLineNotification,
    getRealtimeGroups,
    createRealtimeGroup,
    updateRealtimeGroup,
    deleteRealtimeGroup,
    updateRealtimeGroupDevices,
    updateInformation,
    getOverview,
    addRecord,
    addRealTime,
    deleteNotStarted,
    deleteRecords,
    getLabels,
    createLabel,
    updateLabel,
    deleteLabel,
    getLabelByDeviceID,
    getReportPdfUrl,
    getCertPdfUrl,
    getReportExcelUrl,
    addLabelsToRecord,
    removeLabelsFromRecord,
    toggleRealtimeArchive,
    getLineStatus,
    createLineBindCode,
    unlinkLine,
};
